import type { StationRole } from '@/objects/generated';
import { SavedMatrixSettingsV1 } from './SavedMatrixSettingsV1';
import { SavedMatrixSettingsV2 } from './SavedMatrixSettingsV2';
import { SettingsBase } from './SettingsBase';
import type { RacesGroupsCategoriesMap } from '@/utilities/RacesGroupsCategoriesMap';
import { MatrixSettings } from './MatrixSettings';
import type { CategoryId, GroupId, RaceId, StationRoleId } from '@/utilities/typealiases';
import { arrayDiff, arrayIntersect } from '@/utilities';

/**
 * Wrapper class to hold the different versions of the MatrixSettings.
 * This is used to deserialize the settings and determine which version to use.
 * It also maps to and from the MatrixSettings object that we are actually using at runtime
 */
export class SavedMatrixSettings extends SettingsBase {
    static NAME_OF_CLASS = 'SavedMatrixSettings';

    private SavedSettings: SavedMatrixSettingsV1 | SavedMatrixSettingsV2;

    public constructor(val?: SavedMatrixSettingsV1 | SavedMatrixSettingsV2) {
        super();

        this.SavedSettings = val ?? new SavedMatrixSettingsV2(); // The val parameter needs to be nullable since we are creating this object to call the deserialize method
    }

    public static FromMatrixSettings(
        matrixSettings: MatrixSettings,
        racesGroupsCategoriesMap: RacesGroupsCategoriesMap,
        stationRoles: StationRole[],
    ): SavedMatrixSettings {
        const savedSettings = new SavedMatrixSettings();
        savedSettings.SavedSettings = SavedMatrixSettingsV2.FromMatrixSettings(matrixSettings, racesGroupsCategoriesMap, stationRoles);

        return savedSettings;
    }

    /**
     * Builds a MatrixSettings object from the saved settings and the current state of the event.
     * This will also handle backwards compatibility with older versions of the settings.
     * @param racesGroupsCategoriesMap The racesGroupsCategoriesMap for how the event looks like now
     * @param stationRoles All the stationroles for the event
     * @returns A MatrixSettings object
     */
    public GetMatrixSettings(racesGroupsCategoriesMap: RacesGroupsCategoriesMap, stationRoles: StationRole[]): MatrixSettings {
        let categoryMap = new Map<RaceId, CategoryId[]>();
        let raceGroupMap = new Map<RaceId, GroupId[]>();
        let stationRolesMap = new Map<RaceId, StationRoleId[]>();
        if (this.SavedSettings instanceof SavedMatrixSettingsV1) {
            // In V1 we only have the selected options, so the best we can do is just use whatever is saved
            categoryMap = this.SavedSettings.CategoryMap;
            raceGroupMap = this.SavedSettings.RaceGroupMap;
            stationRolesMap = this.SavedSettings.StationRolesMap;
        } else if (this.SavedSettings instanceof SavedMatrixSettingsV2) {
            // In V2 we have every option, with an enabled/disabled flag, so we can know if we have added any new options

            categoryMap = new Map<RaceId, CategoryId[]>();
            this.SavedSettings.CategoryMap.forEach((categoryIds, raceId) => {
                // Check for new options
                const newCategories = arrayDiff(
                    Array.from(racesGroupsCategoriesMap.GetCategoryIDs([], raceId)),
                    categoryIds.map((cat) => cat.id),
                );

                // Add the options that are enabled in the saved settings
                categoryMap.set(raceId, [...categoryIds.filter((cat) => cat.enabled).map((cat) => cat.id), ...newCategories]);
            });

            raceGroupMap = new Map<RaceId, GroupId[]>();
            this.SavedSettings.RaceGroupMap.forEach((groupIds, raceId) => {
                // Check for new options
                const newGroups = arrayDiff(
                    Array.from(racesGroupsCategoriesMap.GetGroupIDs(raceId)),
                    groupIds.map((group) => group.id),
                );

                // Add the options that are enabled in the saved settings
                raceGroupMap.set(raceId, [...groupIds.filter((group) => group.enabled).map((group) => group.id), ...newGroups]);
            });

            stationRolesMap = new Map<RaceId, StationRoleId[]>();
            this.SavedSettings.StationRolesMap.forEach((stationRoleIds, raceId) => {
                // Check for new options
                const newStationRoles = arrayDiff(
                    Array.from(stationRoles.filter((sr) => sr.RaceID === raceId || sr.RaceID === null).map((role) => role.ID)),
                    stationRoleIds.map((role) => role.id),
                );

                // Add the options that are enabled in the saved settings
                stationRolesMap.set(raceId, [...stationRoleIds.filter((role) => role.enabled).map((role) => role.id), ...newStationRoles]);
            });
        }

        // Initalize the selections.
        // This prefills the matrixSettings with all the stationRoles, RaceGroups and Categories, unless we have saved values.
        // It also removes any invalid saved values. (someone might have deleted a category after the settings were saved for example)
        racesGroupsCategoriesMap.GetRaceIDs().forEach((raceID) => {
            const allStatioRoleIDsForRace = stationRoles.filter((sr) => sr.RaceID === raceID || sr.RaceID == null).map((sr) => sr.ID);
            const allGroupIDsForRace = racesGroupsCategoriesMap.GetGroupIDs(raceID);
            const allCategoryIDsForRace = racesGroupsCategoriesMap.GetCategoryIDs(allGroupIDsForRace, raceID);

            if (!stationRolesMap.has(raceID)) {
                stationRolesMap.set(raceID, allStatioRoleIDsForRace);
            } else {
                // This removes any invalid saved values
                stationRolesMap.set(raceID, arrayIntersect(stationRolesMap.get(raceID)!, allStatioRoleIDsForRace));
            }

            if (!raceGroupMap.has(raceID)) {
                raceGroupMap.set(raceID, allGroupIDsForRace);
            } else {
                // This removes any invalid saved values
                raceGroupMap.set(raceID, arrayIntersect(raceGroupMap.get(raceID)!, allGroupIDsForRace));
            }

            if (!categoryMap.has(raceID)) {
                categoryMap.set(raceID, allCategoryIDsForRace);
            } else {
                // This removes any invalid saved values
                categoryMap.set(raceID, arrayIntersect(categoryMap.get(raceID)!, allCategoryIDsForRace));
            }
        });

        return new MatrixSettings({
            categoryMap: categoryMap,
            raceGroupMap: raceGroupMap,
            stationRolesMap: stationRolesMap,
            matrixConfig: this.SavedSettings.MatrixConfig,
        });
    }

    public serialize(): string {
        return this.SavedSettings.serialize();
    }

    public deserialize(json: string) {
        // Parse as pojo so we can read the version
        const tempParsed = JSON.parse(json);
        const version = tempParsed.Version ?? 1;

        switch (version) {
            case 1:
                this.SavedSettings = new SavedMatrixSettingsV1(new SavedMatrixSettingsV1().deserialize(json));
                break;
            case 2:
                this.SavedSettings = new SavedMatrixSettingsV2(new SavedMatrixSettingsV2().deserialize(json));
                break;
            default:
                throw new Error(`Unsupported version: ${version}`);
        }

        return this.SavedSettings;
    }
}
